/* eslint-disable */
import { useCallback, useMemo, useRef, useState } from "react";
import ReactECharts from "echarts-for-react";

export interface CollateralGapBarRow {
  planYear: number;
  year: number;
  insuredAge: number;
  surrenderValue: number | null;
  outsideAssets: number | null;
  financingBalance: number | null;
  difference: number | null;
  status: string | null;
  percentage: number | null;
}

interface CollateralGapChartProps {
  data: CollateralGapBarRow[];
  isDark: boolean;
  loading?: boolean;
}

interface ChipPosition {
  x: number;
  leftX: number;
  rightX: number;
  y: number;
  idx: number;
}

const SURRENDER_COLOR = "#3B82F6";
const OUTSIDE_COLOR = "#93C5FD";
const FINANCING_COLOR = "#94A3B8";
const SHORTFALL_COLOR = "#E24C4C";
const SURPLUS_COLOR = "#1EA97C";
const PRIMARY = "#1E3A8A";
const INITIAL_VISIBLE = 10;

const fmt = (v: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.abs(v));

const fmtAxis = (v: number) => {
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${v / 1_000}K`;
  return `$${v}`;
};

function CollateralGapChart({ data, isDark, loading }: CollateralGapChartProps) {
  const echartsRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const zoomRef = useRef<{ start: number; end: number } | null>(null);

  const [chips, setChips] = useState<ChipPosition[]>([]);

  const option = useMemo(() => {
    const textColor = isDark ? "#e2e8f0" : "#334155";
    const gridColor = isDark ? "#3A3A50" : "#e2e8f0";
    const tooltipBg = isDark ? "#2A2A3D" : "#ffffff";
    const tooltipBorder = isDark ? "#3A3A50" : "#E5E7EB";
    const headerBg = isDark ? "#3A3A50" : "#E5E7EB";
    const headerText = isDark ? "#ffffff" : "#374151";
    const sepColor = isDark ? "rgba(255,255,255,0.4)" : "rgba(156,163,175,0.4)";
    const bodyText = isDark ? "#ffffff" : "#374151";
    const rowBorder = isDark ? "#4A4A60" : "#E5E7EB";

    const useInitialWindow = !zoomRef.current;
    const initialEndIdx = Math.min(INITIAL_VISIBLE - 1, Math.max(data.length - 1, 0));

    const tooltipFormatter = (params: any) => {
      const idx = Array.isArray(params) ? params[0]?.dataIndex : params?.dataIndex;
      const row = data[idx];
      if (!row) return "";

      const isSurplus = row.status === "surplus" || (row.difference != null && row.difference >= 0);
      const accentColor = isSurplus ? SURPLUS_COLOR : SHORTFALL_COLOR;
      const diff = row.difference ?? 0;

      const header = `
        <div style="
          background:${headerBg};
          color:${headerText};
          padding:8px 12px;
          border-bottom:1px solid ${rowBorder};
          border-radius:8px 8px 0 0;
          display:flex;
          justify-content:center;
          align-items:center;
          gap:12px;
          font-family:'Conthrax Semi-Bold','Arial',sans-serif;
          font-weight:bold;
        ">
          <div style="display:flex;align-items:center;gap:6px;">
            <i class="pi pi-calendar" style="font-size:16px;color:${headerText};"></i>
            <span>${row.year}</span>
          </div>
          <div style="width:1px;height:20px;background:${sepColor};"></div>
          <div style="display:flex;align-items:center;gap:6px;">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style="transform:translateY(1px);">
              <rect x="4" y="12" width="16" height="3" stroke="${headerText}" stroke-width="1.5" fill="none"/>
              <rect x="6" y="8" width="12" height="4" stroke="${headerText}" stroke-width="1.5" fill="none"/>
              <rect x="8" y="4" width="8" height="4" stroke="${headerText}" stroke-width="1.5" fill="none"/>
            </svg>
            <span>${row.planYear}</span>
          </div>
          <div style="width:1px;height:20px;background:${sepColor};"></div>
          <div style="display:flex;align-items:center;gap:6px;">
            <i class="pi pi-user" style="font-size:16px;color:${headerText};"></i>
            <span>A${row.insuredAge}</span>
          </div>
        </div>`;

      const seriesRows = [
        { label: "Surrender Value", color: SURRENDER_COLOR, value: row.surrenderValue },
        { label: "Outside Assets", color: OUTSIDE_COLOR, value: row.outsideAssets },
        { label: "Financing Balance", color: FINANCING_COLOR, value: row.financingBalance },
      ];

      let body = `<div style="padding:8px 12px;">`;
      seriesRows.forEach(({ label, color, value }) => {
        body += `
          <div style="
            display:flex;
            justify-content:space-between;
            align-items:center;
            padding:4px 8px;
            margin:2px 0;
            border-bottom:1px solid ${rowBorder};
          ">
            <div style="display:flex;align-items:center;gap:8px;">
              <div style="width:8px;height:8px;border-radius:50%;background:${color};flex-shrink:0;"></div>
              <span style="color:${bodyText};font-size:12px;font-family:'Conthrax Semi-Bold','Arial',sans-serif;">${label}</span>
            </div>
            <span style="color:${bodyText};font-size:12px;font-weight:600;margin-left:12px;font-family:'Conthrax Semi-Bold','Arial',sans-serif;">
              ${value != null ? fmt(value) : "—"}
            </span>
          </div>`;
      });

      body += `
        <div style="
          display:flex;
          justify-content:space-between;
          align-items:center;
          padding:6px 8px 2px;
          margin-top:4px;
        ">
          <span style="color:${accentColor};font-size:12px;font-weight:600;">
            ${isSurplus ? "Surplus" : "Shortfall"}
          </span>
          <span style="color:${accentColor};font-size:13px;font-weight:700;margin-left:12px;">
            ${isSurplus ? `+${fmt(Math.abs(diff))}` : `-${fmt(Math.abs(diff))}`}
          </span>
        </div>
      </div>`;

      return `<div style="
        background:${tooltipBg};
        border:1px solid ${tooltipBorder};
        border-radius:8px;
        box-shadow:0 4px 12px rgba(0,0,0,0.15);
        overflow:hidden;
        min-width:230px;
      ">${header}${body}</div>`;
    };

    return {
      animation: false,
      tooltip: {
        show: true,
        trigger: "axis" as const,
        axisPointer: { type: "shadow" as const },
        formatter: tooltipFormatter,
        backgroundColor: "transparent",
        borderWidth: 0,
        padding: 0,
        extraCssText: "box-shadow:none;",
      },
      legend: { show: false },
      grid: { left: 60, right: 24, top: 56, bottom: 60 },
      dataZoom: [
        {
          type: "slider",
          show: true,
          startValue: useInitialWindow ? 0 : undefined,
          endValue: useInitialWindow ? initialEndIdx : undefined,
          start: !useInitialWindow ? zoomRef.current?.start ?? 0 : undefined,
          end: !useInitialWindow
            ? zoomRef.current?.end ?? Math.min(100, (INITIAL_VISIBLE / (data.length || 1)) * 100)
            : undefined,
          minValueSpan: 1,
          height: 24,
          bottom: 4,
          borderColor: "transparent",
          fillerColor: isDark ? "rgba(167,183,204,0.3)" : "rgba(167,183,204,0.4)",
          handleStyle: {
            color: isDark ? "#ffffff" : "#334155",
            borderColor: isDark ? "#4A4A60" : "#E5E7EB",
          },
          textStyle: { color: textColor, fontSize: 10 },
          showDetail: true,
          showDataShadow: true,
          realtime: true,
          filterMode: "filter",
          xAxisIndex: 0,
        },
      ],
      xAxis: {
        type: "category",
        data: data.map((r) => `Anniversary\n${r.year}`),
        axisLine: { lineStyle: { color: gridColor } },
        axisTick: { show: false },
        axisLabel: { color: textColor, fontSize: 11, lineHeight: 16 },
      },
      yAxis: {
        type: "value",
        axisLabel: { color: textColor, fontSize: 11, formatter: fmtAxis },
        splitLine: { lineStyle: { color: gridColor, type: "dashed" } },
      },
      series: [
        // ── Left group: stacked surrender + outside assets ───────────────────
        {
          name: "Surrender Value",
          type: "bar",
          stack: "collateral",
          barMaxWidth: 52,
          barCategoryGap: "25%",
          barGap: "15%",
          data: data.map((r) => r.surrenderValue ?? 0),
          itemStyle: { color: SURRENDER_COLOR },
        },
        {
          name: "Outside Assets",
          type: "bar",
          stack: "collateral",
          barMaxWidth: 52,
          barCategoryGap: "25%",
          barGap: "15%",
          data: data.map((r) => r.outsideAssets ?? 0),
          itemStyle: { color: OUTSIDE_COLOR, borderRadius: [3, 3, 0, 0] },
        },
        // ── Right bar: financing balance ─────────────────────────────────────
        {
          name: "Financing Balance",
          type: "bar",
          barMaxWidth: 52,
          barCategoryGap: "25%",
          barGap: "15%",
          data: data.map((r) => r.financingBalance ?? 0),
          itemStyle: { color: FINANCING_COLOR, borderRadius: [3, 3, 0, 0] },
        },
      ],
    };
  }, [data, isDark]);

  // ── Chip recomputation ───────────────────────────────────────────────────────

  const recomputeChips = useCallback(() => {
    const chart = echartsRef.current?.getEchartsInstance?.();
    if (!chart) return;

    const newChips: ChipPosition[] = [];

    data.forEach((row, i) => {
      const leftTotal = (row.surrenderValue ?? 0) + (row.outsideAssets ?? 0);
      const rightTotal = row.financingBalance ?? 0;
      if (leftTotal <= 0 && rightTotal <= 0) return;

      const leftPx = chart.convertToPixel({ seriesIndex: 0 }, [i, leftTotal]) as
        | [number, number]
        | null;
      const rightPx = chart.convertToPixel({ seriesIndex: 2 }, [i, rightTotal]) as
        | [number, number]
        | null;

      if (!leftPx || !rightPx) return;

      const chipX = (leftPx[0] + rightPx[0]) / 2;
      const chipY = Math.min(leftPx[1], rightPx[1]);

      newChips.push({ x: chipX, leftX: leftPx[0], rightX: rightPx[0], y: chipY, idx: i });
    });

    setChips(newChips);
  }, [data]);

  // ── Chart events ─────────────────────────────────────────────────────────────

  const chartEvents = useMemo(
    () => ({
      finished: recomputeChips,
      dataZoom: (params: any) => {
        zoomRef.current = { start: params.start, end: params.end };
        recomputeChips();
      },
    }),
    [recomputeChips],
  );

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div ref={containerRef} className="relative w-full">
      <ReactECharts
        ref={echartsRef}
        option={option}
        style={{ height: 320, cursor: "default" }}
        notMerge
        lazyUpdate
        showLoading={loading}
        loadingOption={{
          text: "",
          color: PRIMARY,
          maskColor: isDark ? "rgba(30,32,48,0.8)" : "rgba(255,255,255,0.8)",
        }}
        onEvents={chartEvents}
      />

      {/* ── Floating chips ────────────────────────────────────────────────── */}
      {chips.map((chip) => {
        const row = data[chip.idx];
        if (!row) return null;

        const isSurplus =
          row.status === "surplus" || (row.difference != null && row.difference >= 0);
        const accentColor = isSurplus ? SURPLUS_COLOR : SHORTFALL_COLOR;
        const pct = row.percentage != null ? Math.round(Math.abs(row.percentage) * 100) : null;
        const diff = row.difference ?? 0;
        const innerFill = isSurplus ? "#A0E6BA" : "#fae0d4";

        return (
          <div
            key={chip.idx}
            style={{
              position: "absolute",
              left: chip.x,
              top: chip.y - 8,
              transform: "translate(-50%, -100%)",
              display: "flex",
              alignItems: "center",
              pointerEvents: "none",
              zIndex: 10,
              gap: 4,
            }}
          >
            {/* Hexagon SVG badge — matches CollateralForcast style */}
            <svg width="42" height="48" viewBox="0 0 42 48" style={{ flexShrink: 0 }}>
              <polygon
                points="21,2 39,12 39,36 21,46 3,36 3,12"
                fill={innerFill}
                stroke={accentColor}
                strokeWidth="2"
              />
              <text
                x="21"
                y="28"
                textAnchor="middle"
                fill={accentColor}
                fontSize="11"
                fontWeight="bold"
                fontFamily="sans-serif"
              >
                {pct != null ? `${pct}%` : "—"}
              </text>
            </svg>

            {/* Label box */}
            <div
              style={{
                border: `2px solid ${accentColor}`,
                borderRadius: 8,
                backgroundColor: isDark ? "#1E2030" : "#ffffff",
                padding: "4px 10px",
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <span style={{ color: isDark ? "#94A3B8" : "#64748B", fontSize: 9, fontWeight: 600 }}>
                {isSurplus ? "Surplus" : "Shortfall"}:
              </span>
              <span
                style={{ color: accentColor, fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}
              >
                {isSurplus ? `+${fmt(Math.abs(diff))}` : `-${fmt(Math.abs(diff))}`}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default CollateralGapChart;
